import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage, SUPPORTED_LANGS } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import SEOHead from "@/components/SEOHead";

// Landing Pages
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import FAQPage from "@/pages/FAQPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

// Auth Pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";

// Shop Pages
import SetupWizard from "@/pages/shop/SetupWizard";
import ShopDashboard from "@/pages/shop/ShopDashboard";
import POS from "@/pages/shop/POS";
import ShopProductsPage from "@/pages/shop/ShopProductsPage";
import ShopInvoicesPage from "@/pages/shop/ShopInvoicesPage";
import StockPage from "@/pages/shop/StockPage";
import ReportsPage from "@/pages/shop/ReportsPage";
import StaffPage from "@/pages/shop/StaffPage";
import PrintSettingsPage from "@/pages/shop/PrintSettingsPage";
import DeliveryPage from "@/pages/shop/DeliveryPage";
import PaymentSettingsPage from "@/pages/shop/PaymentSettingsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";

// Invoice Portal Pages
import InvoicePortalDashboard from "@/pages/invoice/InvoicePortalDashboard";
import InvoiceManagePage from "@/pages/invoice/InvoiceManagePage";
import InvoiceSetup from "@/pages/invoice/InvoiceSetup";
import PublicInvoicePage from "@/pages/invoice/PublicInvoicePage";
import SuperAdminLogin from "@/pages/superadmin/SuperAdminLogin";
import SuperAdminDashboard from "@/pages/superadmin/SuperAdminDashboard";
import SuperAdminShops from "@/pages/superadmin/SuperAdminShops";
import SuperAdminUsers from "@/pages/superadmin/SuperAdminUsers";
import SuperAdminRevenue from "@/pages/superadmin/SuperAdminRevenue";
import SuperAdminTickets from "@/pages/superadmin/SuperAdminTickets";
import SuperAdminCatalog from "@/pages/superadmin/SuperAdminCatalog";
import SuperAdminSubscriptions from "@/pages/superadmin/SuperAdminSubscriptions";
import SubscriptionPage from "@/pages/shop/SubscriptionPage";
import TicketsPage from "@/pages/shop/TicketsPage";
import SmtpSettingsPage from "@/pages/shop/SmtpSettingsPage";
import EmailTemplatesPage from "@/pages/shop/EmailTemplatesPage";
import VeriFactuSettings from "@/pages/shop/VeriFactuSettings";
import VeriFactuDashboard from "@/pages/shop/VeriFactuDashboard";
import ShopSettingsPage from "@/pages/shop/ShopSettingsPage";
import SocialPostsPage from "@/pages/shop/SocialPostsPage";
import DocsPage from "@/pages/shop/DocsPage";
import MobileAppPage from "@/pages/MobileAppPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Auto-detect browser language and redirect "/" to "/<lang>"
const RootRedirect = () => {
  const saved = localStorage.getItem('vf_language');
  if (saved && SUPPORTED_LANGS.includes(saved)) {
    return <Navigate to={`/${saved}`} replace />;
  }
  const browserLang = navigator.language.split('-')[0];
  const lang = SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'es';
  return <Navigate to={`/${lang}`} replace />;
};

// Sync URL lang param to LanguageContext
const LangSync = ({ children }) => {
  const { lang } = useParams();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && lang !== language) {
      setLanguage(lang);
    }
  }, [lang, language, setLanguage]);

  return children;
};

// Wrapper for language-prefixed pages with SEO
const LangPage = ({ component: Component, page }) => (
  <LangSync>
    <SEOHead page={page} />
    <Component />
  </LangSync>
);

// Placeholder components
const StaffPinLogin = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Staff PIN Login</h1>
      <p className="text-slate-600">Coming soon...</p>
      <a href="/login" className="text-[#22c55e] hover:underline mt-4 block">Back to Login</a>
    </div>
  </div>
);

const CookiesPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Cookie Policy</h1>
      <p className="text-slate-600">Coming soon...</p>
      <a href="/" className="text-[#22c55e] hover:underline mt-4 block">Back to Home</a>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <Toaster richColors position="top-right" closeButton />
            <BrowserRouter>
              <Routes>
                {/* Root: auto-detect language and redirect */}
                <Route path="/" element={<RootRedirect />} />

                {/* Language-prefixed public pages */}
                <Route path="/:lang" element={<LangPage component={LandingPage} page="home" />} />
                <Route path="/:lang/pricing" element={<LangPage component={PricingPage} page="pricing" />} />
                <Route path="/:lang/about" element={<LangPage component={AboutPage} page="about" />} />
                <Route path="/:lang/contact" element={<LangPage component={ContactPage} page="contact" />} />
                <Route path="/:lang/faq" element={<LangPage component={FAQPage} page="faq" />} />
                <Route path="/:lang/terms" element={<LangPage component={TermsPage} page="terms" />} />
                <Route path="/:lang/privacy" element={<LangPage component={PrivacyPage} page="privacy" />} />
                <Route path="/:lang/cookies" element={<LangPage component={CookiesPage} page="cookies" />} />
                <Route path="/:lang/mobile" element={<LangPage component={MobileAppPage} page="mobile" />} />

                {/* Auth Pages (no lang prefix - internal) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                {/* Payment Pages */}
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />

                {/* Shop Admin Dashboard (no lang prefix - internal app) */}
                <Route path="/shop/login" element={<LoginPage />} />
                <Route path="/shop/staff-login" element={<StaffPinLogin />} />
                <Route path="/shop/setup" element={<SetupWizard />} />
                <Route path="/shop/dashboard" element={<ShopDashboard />} />
                <Route path="/shop/pos" element={<POS />} />
                <Route path="/shop/products" element={<ShopProductsPage />} />
                <Route path="/shop/products/*" element={<ShopProductsPage />} />
                <Route path="/shop/stock" element={<StockPage />} />
                <Route path="/shop/reports" element={<ReportsPage />} />
                <Route path="/shop/staff" element={<StaffPage />} />
                <Route path="/shop/print-settings" element={<PrintSettingsPage />} />
                <Route path="/shop/delivery" element={<DeliveryPage />} />
                <Route path="/shop/payment-settings" element={<PaymentSettingsPage />} />
                <Route path="/shop/tickets" element={<TicketsPage />} />
                <Route path="/shop/smtp-settings" element={<SmtpSettingsPage />} />
                <Route path="/shop/email-templates" element={<EmailTemplatesPage />} />
                <Route path="/shop/verifactu" element={<VeriFactuDashboard />} />
                <Route path="/shop/verifactu/settings" element={<VeriFactuSettings />} />
                <Route path="/shop/settings" element={<ShopSettingsPage />} />
                <Route path="/shop/social" element={<SocialPostsPage />} />
                <Route path="/shop/help" element={<DocsPage />} />
                <Route path="/shop/subscription" element={<SubscriptionPage />} />
                <Route path="/shop/subscription/success" element={<SubscriptionPage />} />
                <Route path="/shop/invoices" element={<ShopInvoicesPage />} />
                <Route path="/shop/invoices/*" element={<ShopInvoicesPage />} />
                <Route path="/shop/*" element={<ShopDashboard />} />

                {/* Public Invoice Portal (no auth) */}
                <Route path="/invoice/view/:token" element={<PublicInvoicePage />} />

                {/* Super Admin */}
                <Route path="/sad/login" element={<SuperAdminLogin />} />
                <Route path="/sad/dashboard" element={<SuperAdminDashboard />} />
                <Route path="/sad/shops" element={<SuperAdminShops />} />
                <Route path="/sad/users" element={<SuperAdminUsers />} />
                <Route path="/sad/revenue" element={<SuperAdminRevenue />} />
                <Route path="/sad/tickets" element={<SuperAdminTickets />} />
                <Route path="/sad/catalog" element={<SuperAdminCatalog />} />
                <Route path="/sad/subscriptions" element={<SuperAdminSubscriptions />} />

                {/* Invoice Portal */}
                <Route path="/invoices/login" element={<LoginPage />} />
                <Route path="/invoices/setup" element={<InvoiceSetup />} />
                <Route path="/invoices/dashboard" element={<InvoicePortalDashboard />} />
                <Route path="/invoices/manage" element={<InvoiceManagePage />} />
                <Route path="/invoices/settings" element={<InvoicePortalDashboard />} />
                <Route path="/invoices/*" element={<InvoicePortalDashboard />} />

                {/* Catch all - redirect to root (will auto-detect lang) */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <PWAInstallPrompt />
            </BrowserRouter>
          </div>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
